import { Box, FormHelperText, styled, SvgIcon, SxProps, Theme } from '@mui/material';
import React from 'react';
import characters from 'src/constants/characters';
import { InformationCircleIcon } from 'src/icons';
import { AnalyticsElementType } from 'src/lib/analytics/events';
import StyledTooltip from '../StyledTooltip';
import { Label } from './Label';

const Container = styled('div')<{ wide: boolean }>`
    ${({ wide }) => `
        width: ${wide ? '100%' : 'auto'};
    `}
`;

const Body = styled('div')`
    
`;

export interface FieldProps {
    editable?: boolean;
    error?: string | boolean | null | string[];
    helperText?: string;
    helperTextStyle?: SxProps<Theme>;
    label?: string;
    wide?: boolean;
    required?: boolean;
    tooltip?: React.ReactNode;
    labelAdornment?: React.ReactNode;
    inlineLabelAdornment?: boolean;
    elementType?: AnalyticsElementType;
    name?: string;
    tooltipMaxWidth?: string;
}
interface Parent {
    children: React.ReactNode;
}
export type FullFieldProps = FieldProps & Parent;

export type FormErrorTextProps = Pick<FieldProps, 'editable' | 'error'>;

export const FormErrorText = ({ editable, error }: FormErrorTextProps) => {
    if (!editable || !error || (Array.isArray(error) && !error.length)) {
        return null;
    }
    const errorStrings = Array.isArray(error) ? error : [error];
    return (
        <>
            {errorStrings
                .filter((e) => typeof e === 'string')
                .map((e, i) => {
                    return (
                        <FormHelperText error key={`form_error_${i}_${e}`}>
                            {e}
                        </FormHelperText>
                    );
                })}
        </>
    );
};

export const Field = ({
    editable = false,
    error,
    helperText,
    helperTextStyle,
    labelAdornment,
    inlineLabelAdornment = false,
    label,
    tooltip,
    children,
    wide = false,
    required = false,
    elementType,
    name,
    tooltipMaxWidth,
}: FullFieldProps) => {
    const isChildrenEmpty =
        children === '' ||
        children === null ||
        children === undefined ||
        children === false ||
        React.Children.count(children) === 0;

    const renderedLabel =
        inlineLabelAdornment && label ? (
            <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                <Box component="span">{label}</Box>
                {labelAdornment}
            </Box>
        ) : (
            label
        );

    const justifyContentValue =
        labelAdornment && !inlineLabelAdornment ? 'space-between' : undefined;

    const hasError = !!error && (Array.isArray(error) ? error.length > 0 : true);

    return (
        <Container
            wide={wide}
            data-field-label={label}
            data-field-role={elementType}
            data-field-name={name}
            data-error={hasError || undefined}
        >
            <Box display="flex" alignItems="center" justifyContent={justifyContentValue}>
                {label && (
                    <Label
                        editable={editable}
                        error={!!error}
                        required={required}
                        inline={!!tooltip}
                    >
                        {renderedLabel}
                    </Label>
                )}
                {!inlineLabelAdornment && labelAdornment}
                {tooltip && (
                    <Box marginLeft={0.5} marginTop={0.25}>
                        <StyledTooltip
                            title={tooltip}
                            placement="top"
                            arrow
                            slotProps={{ popper: { style: { maxWidth: tooltipMaxWidth } } }}
                        >
                            <Box justifyContent="center" alignItems="center" fontSize="16px">
                                <SvgIcon fontSize="inherit">
                                    <InformationCircleIcon />
                                </SvgIcon>
                            </Box>
                        </StyledTooltip>
                    </Box>
                )}
            </Box>
            <Body>{isChildrenEmpty ? characters.emdash : children}</Body>
            <FormErrorText error={error} editable={editable} />
            {helperText && (
                <FormHelperText sx={{ marginTop: 1, ...helperTextStyle }}>
                    {helperText}
                </FormHelperText>
            )}
        </Container>
    );
};
